import type { Locale } from '@peloton/internationalize/models/locale';
import { BundleType } from '@ecomm/shop/models';
import { toCommercetoolsClient } from '../apollo/toCommercetoolsClient';
import type {
  GetCustomObjectByKeyQuery,
  GetCustomObjectByKeyQueryVariables,
} from '../queries/GetCustomObjectByKey.generated';
import { GetCustomObjectByKeyDocument } from '../queries/GetCustomObjectByKey.generated';

export type BundleTypeMap = Partial<Record<BundleType, string>>;

const CUSTOM_OBJECT_CONTAINER = 'basics-packages';
const CUSTOM_OBJECT_KEY = 'basics-packages';

// CT forces keys with hyphens to be camel cased instead
type BasicsPackagesKeys =
  | 'bike'
  | 'bikePlus'
  | 'refurbishedBike'
  | 'refurbishedBikePlus'
  | 'tread'
  | 'treadPlus'
  | 'row';

type BasicsPackagesCustomObject = Record<
  BasicsPackagesKeys,
  Partial<Record<Locale, string>>
>;

const bundleTypeMapping: Record<BasicsPackagesKeys, BundleType> = {
  bike: BundleType.Bike,
  bikePlus: BundleType.BikePlus,
  refurbishedBike: BundleType.RefurbishedBike,
  refurbishedBikePlus: BundleType.RefurbishedBikePlus,
  tread: BundleType.Tread,
  treadPlus: BundleType.TreadPlus,
  row: BundleType.Row,
};

const transformCustomObject = (value: BasicsPackagesCustomObject, locale: Locale) => {
  const bundleTypeMap: BundleTypeMap = {};

  for (const [key, localizedSlugs] of Object.entries(value)) {
    const bundleType = bundleTypeMapping[key as BasicsPackagesKeys];
    const basicsSlug = localizedSlugs[locale];

    if (!bundleType || !basicsSlug) {
      continue;
    }

    bundleTypeMap[bundleType] = basicsSlug;
  }

  return bundleTypeMap;
};

const fetchBasicsPackagesMap = async (locale: Locale) => {
  const client = toCommercetoolsClient();

  const res = await client.query<
    GetCustomObjectByKeyQuery,
    GetCustomObjectByKeyQueryVariables
  >({
    query: GetCustomObjectByKeyDocument,
    variables: {
      key: CUSTOM_OBJECT_KEY,
      container: CUSTOM_OBJECT_CONTAINER,
    },
  });

  if (!res.data.customObject) {
    throw new Error('Basics packages not found');
  }

  return transformCustomObject(res.data.customObject.value, locale);
};

export default fetchBasicsPackagesMap;
